import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Grid3x3, Save, Trash2, X, AlertTriangle } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { CurrencyPicker } from '@/components/CurrencyPicker';
import { DimensionPicker } from '@/components/app/DimensionPicker';
import { PivotGrid } from '@/components/app/PivotGrid';
import { UpgradeWall } from '@/components/app/UpgradeWall';
import { isPlanGateError } from '@/lib/planGate';
import { pivotApi, PivotConfig, PivotCell, PivotKey, SavedView } from '@/lib/pivot';
import { orgChangeApi } from '@/lib/orgChange';
import { qaApi, FactRow } from '@/lib/qa';
import { ExplainCellPopover } from '@/components/ExplainCellPopover';
import { ExplainCellContext } from '@/lib/explainCell';

const today = new Date().toISOString().slice(0, 10);
// Default to a trailing 12 months so the first view shows a full year of data
// (matches the P&L page idiom) rather than a confusing year-to-date partial.
const oneYearAgo = `${Number(today.slice(0, 4)) - 1}${today.slice(4)}`;

const DEFAULT_CONFIG: PivotConfig = {
  rowDims: ['account_code'],
  colDims: ['scenario'],
  periodStart: oneYearAgo,
  periodEnd: today,
  periodBucket: 'none',
  scenarios: [],
  filters: {},
  reportingCurrency: 'JPY',
  normalizeCurrency: false,
  eliminateIntercompany: false,
};

export function PivotPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const [cfg, setCfg] = useState<PivotConfig>(DEFAULT_CONFIG);
  const [drill, setDrill] = useState<{ title: string; ids: string[] } | null>(null);
  const [explain, setExplain] = useState<(ExplainCellContext & { label: string }) | null>(null);
  const [showSave, setShowSave] = useState(false);
  const set = <K extends keyof PivotConfig>(k: K, v: PivotConfig[K]) => setCfg((c) => ({ ...c, [k]: v }));
  const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  const inputCls = 'rounded-lg border border-border bg-white px-3 py-2 text-sm';

  const axes = useQuery({ queryKey: ['pivot-axes', wsId], queryFn: () => pivotApi.axes(wsId!), enabled: !!wsId, retry: false });
  const scenarios = useQuery({ queryKey: ['pivot-scenarios', wsId], queryFn: () => pivotApi.scenarios(wsId!), enabled: !!wsId, retry: false });
  const views = useQuery({ queryKey: ['pivot-views', wsId], queryFn: () => pivotApi.views.list(wsId!), enabled: !!wsId, retry: false });
  const orgChanges = useQuery({ queryKey: ['org-changes', wsId], queryFn: () => orgChangeApi.list(wsId!), enabled: !!wsId, retry: false });
  const hasOrgChanges = (orgChanges.data ?? []).length > 0;
  const planGated = [axes.error, scenarios.error, views.error].some((e) => e && isPlanGateError(e));

  const canRun = cfg.rowDims.length > 0 && cfg.scenarios.length > 0;
  const matrix = useQuery({
    queryKey: ['pivot', wsId, JSON.stringify(cfg)],
    queryFn: () => pivotApi.compute(wsId!, cfg),
    enabled: !!wsId && canRun,
    retry: false,
  });
  const matrixGated = matrix.error && isPlanGateError(matrix.error);
  const computeErr = matrix.error && !matrixGated
    ? ((matrix.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (matrix.error as Error).message)
    : null;

  const toggleScenario = (s: string) =>
    setCfg((c) => ({ ...c, scenarios: c.scenarios.includes(s) ? c.scenarios.filter((x) => x !== s) : [...c.scenarios, s] }));

  const openDrill = (cell: PivotCell, row: PivotKey, col: PivotKey) => {
    if (!cell.factIds.length) return;
    setDrill({ title: `${row.values.join(' / ') || '—'} · ${col.values.join(' / ') || '—'}`, ids: cell.factIds });
  };

  // Part 12 — "explain this number". Pull the account code + scenario from
  // whichever axis carries them, default the rest from the current config.
  const openExplain = (cell: PivotCell, row: PivotKey, col: PivotKey) => {
    if (!cell.factIds.length) return;
    const valueFor = (key: string): string | undefined => {
      const ri = cfg.rowDims.indexOf(key); if (ri >= 0) return row.values[ri];
      const ci = cfg.colDims.indexOf(key); if (ci >= 0) return col.values[ci];
      return undefined;
    };
    const label = `${row.values.join(' / ') || '—'} · ${col.values.join(' / ') || '—'}`;
    setExplain({
      label,
      accountCode: valueFor('account_code') ?? label,
      periodStart: cfg.periodStart,
      periodEnd: cfg.periodEnd,
      scenarioA: valueFor('scenario') ?? cfg.scenarios[0] ?? 'actual',
      amountA: cell.amount,
      factIds: cell.factIds,
    });
  };

  const loadView = (v: SavedView) => setCfg({ ...DEFAULT_CONFIG, ...v.config });

  const isGated = planGated || matrixGated;

  return (
    <AppShell title={t('pivot.title', { defaultValue: 'Pivot report' })} subtitle={t('pivot.subtitle', { defaultValue: 'Cross-tab any dimension × period × scenario.' })}>
      <main className="mx-auto max-w-6xl px-6 py-10 space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('pivot.title', { defaultValue: 'Pivot report' })} <span className="text-muted-foreground text-lg">クロス集計</span></h1>
            <p className="mt-1 text-ink-600">{t('pivot.subtitle', { defaultValue: 'Cross-tab any dimension × period × scenario, drill into the underlying facts, and save the view.' })}</p>
          </div>
          {!isGated && (
            <div className="flex items-center gap-2">
              {(views.data ?? []).length > 0 && (
                <select className={`${inputCls} max-w-[200px]`} value="" onChange={(e) => { const v = (views.data ?? []).find((x) => x.id === e.target.value); if (v) loadView(v); }}>
                  <option value="">{t('pivot.load_view', { defaultValue: 'Load saved view…' })}</option>
                  {(views.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              )}
              <button onClick={() => setShowSave(true)} disabled={!canRun} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"><Save className="h-4 w-4" /> {t('pivot.save_view', { defaultValue: 'Save view' })}</button>
            </div>
          )}
        </div>

        {isGated && wsId && <UpgradeWall wsId={wsId} requiredTier="growth" featureName={t('pivot.feature_name', { defaultValue: 'Pivot report builder' })} />}

        {!isGated && (
          <>
            {/* Control bar */}
            <div className="rounded-md border border-border bg-white p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <DimensionPicker label={t('pivot.rows', { defaultValue: 'Rows' })} axes={axes.data ?? []} value={cfg.rowDims} exclude={cfg.colDims} onChange={(v) => set('rowDims', v)} />
                <DimensionPicker label={t('pivot.cols', { defaultValue: 'Columns' })} axes={axes.data ?? []} value={cfg.colDims} exclude={cfg.rowDims} onChange={(v) => set('colDims', v)} />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label={t('pl.period_start', { defaultValue: 'From' })}><input type="date" value={cfg.periodStart} onChange={(e) => set('periodStart', e.target.value)} className={`num ${inputCls} w-full`} /></Field>
                <Field label={t('pl.period_end', { defaultValue: 'To' })}><input type="date" value={cfg.periodEnd} onChange={(e) => set('periodEnd', e.target.value)} className={`num ${inputCls} w-full`} /></Field>
                <Field label={t('pivot.bucket', { defaultValue: 'Period grain' })}>
                  <select value={cfg.periodBucket} onChange={(e) => set('periodBucket', e.target.value as PivotConfig['periodBucket'])} className={`${inputCls} w-full`}>
                    <option value="none">{t('pivot.bucket_none', { defaultValue: 'Whole range' })}</option>
                    <option value="month">{t('pivot.bucket_month', { defaultValue: 'Month' })}</option>
                    <option value="quarter">{t('pivot.bucket_quarter', { defaultValue: 'Quarter' })}</option>
                  </select>
                </Field>
                <Field label={t('pivot.currency', { defaultValue: 'Reporting currency' })}>
                  <CurrencyPicker value={cfg.reportingCurrency} onChange={(v) => set('reportingCurrency', v)} className={`num ${inputCls} w-full`} />
                </Field>
              </div>

              <div>
                <span className="block text-xs font-semibold text-ink-700 mb-1">{t('pivot.scenarios', { defaultValue: 'Scenarios' })} <span className="text-muted-foreground font-normal">({t('pivot.scenarios_hint', { defaultValue: 'pick 1–5' })})</span></span>
                <div className="flex flex-wrap gap-2">
                  {(scenarios.data ?? []).length === 0 && <span className="text-[12px] text-muted-foreground italic">{t('pivot.no_scenarios', { defaultValue: 'No data yet.' })}</span>}
                  {(scenarios.data ?? []).map((s) => {
                    const on = cfg.scenarios.includes(s);
                    return (
                      <button key={s} type="button" onClick={() => toggleScenario(s)} className={`num text-[12px] rounded-full px-3 py-1 border ${on ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-ink-700 border-border hover:border-emerald-400'}`}>{s}</button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-1">
                <label className="inline-flex items-center gap-2 text-sm text-ink-700">
                  <input type="checkbox" checked={cfg.normalizeCurrency} onChange={(e) => set('normalizeCurrency', e.target.checked)} className="rounded border-border" />
                  {t('pivot.normalize', { defaultValue: 'Translate to reporting currency (FX)' })}
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-ink-700">
                  <input type="checkbox" checked={cfg.eliminateIntercompany} onChange={(e) => set('eliminateIntercompany', e.target.checked)} className="rounded border-border" />
                  {t('pivot.eliminate_ic', { defaultValue: 'Eliminate intercompany' })}
                </label>
                {hasOrgChanges && (
                  <label className="inline-flex items-center gap-2 text-sm text-ink-700">
                    <input type="checkbox" checked={cfg.orgChangeAsOf === 'restated'} onChange={(e) => set('orgChangeAsOf', e.target.checked ? 'restated' : 'reported')} className="rounded border-border" />
                    {t('pivot.as_restated', { defaultValue: 'As-restated org structure' })}
                    {cfg.orgChangeAsOf === 'restated' && <span className="text-[10px] uppercase tracking-wide font-semibold rounded px-1.5 py-0.5 bg-violet-100 text-violet-700">{t('pivot.restated_badge', { defaultValue: 'Restated' })}</span>}
                  </label>
                )}
                {cfg.normalizeCurrency && <span className="text-[11px] text-muted-foreground">{t('pivot.normalize_hint', { defaultValue: 'Account / period / scenario axes only.' })}</span>}
              </div>
            </div>

            {/* Warnings */}
            {computeErr && <div className="rounded-md border border-warn-200 bg-warn-50 px-4 py-3 text-sm text-warn-700 flex items-center gap-2"><AlertTriangle className="h-4 w-4 shrink-0" /> {computeErr}</div>}
            {matrix.data?.warnings.mixedCurrencyCells ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {t('pivot.mixed_warn', { defaultValue: `${matrix.data.warnings.mixedCurrencyCells} cell(s) mix currencies — enable FX translation for a single-currency total.`, count: matrix.data.warnings.mixedCurrencyCells })}
              </div>
            ) : null}
            {matrix.data?.warnings.missingRates.length ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-800">{t('pivot.missing_rates', { defaultValue: 'Missing FX rates:' })} <span className="num">{matrix.data.warnings.missingRates.map((m) => `${m.from}→${m.to}`).join(', ')}</span></div>
            ) : null}

            {/* Grid */}
            {!canRun ? (
              <div className="rounded-md border-2 border-dashed border-border bg-white p-10 text-center">
                <Grid3x3 className="h-6 w-6 text-ink-300 mx-auto mb-2" />
                <p className="text-ink-700 font-medium">{t('pivot.pick_prompt', { defaultValue: 'Pick at least one row field and one scenario to build the report.' })}</p>
              </div>
            ) : matrix.isLoading ? (
              <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            ) : matrix.data ? (
              <PivotGrid matrix={matrix.data} fmt={fmt} onCellClick={openDrill} onExplain={openExplain} />
            ) : null}
          </>
        )}
      </main>

      {showSave && wsId && canRun && (
        <SaveViewModal wsId={wsId} config={cfg} onClose={() => setShowSave(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ['pivot-views', wsId] }); setShowSave(false); }} t={t} />
      )}
      {!isGated && (views.data ?? []).length > 0 && wsId && (
        <ManageViews wsId={wsId} views={views.data ?? []} onChanged={() => qc.invalidateQueries({ queryKey: ['pivot-views', wsId] })} t={t} />
      )}
      {drill && wsId && <FactsDrawer wsId={wsId} title={drill.title} ids={drill.ids} onClose={() => setDrill(null)} fmt={fmt} t={t} />}
      {explain && wsId && (
        <ExplainCellPopover
          wsId={wsId}
          context={explain}
          onClose={() => setExplain(null)}
          onViewCitations={(title, ids) => { setExplain(null); setDrill({ title, ids }); }}
        />
      )}
    </AppShell>
  );
}

type TFn = (k: string, o?: Record<string, unknown>) => string;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="block text-xs font-semibold text-ink-700 mb-1">{label}</span>{children}</label>;
}

function SaveViewModal({ wsId, config, onClose, onSaved, t }: { wsId: string; config: PivotConfig; onClose: () => void; onSaved: () => void; t: TFn }) {
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => pivotApi.views.create(wsId, { name: name.trim(), config }),
    onSuccess: onSaved,
    onError: (e) => setErr(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) ?? (e as Error).message),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal>
      <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-sm bg-background border border-border rounded-xl shadow-editorial p-5 space-y-4">
        <h3 className="text-sm font-semibold text-ink-800">{t('pivot.save_view', { defaultValue: 'Save view' })}</h3>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t('pivot.view_name', { defaultValue: 'View name' })} className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm" />
        {err && <p className="text-xs text-warn-700">{err}</p>}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-sm text-ink-700 hover:bg-foreground/[0.04]">{t('common.cancel')}</button>
          <button onClick={() => save.mutate()} disabled={!name.trim() || save.isPending} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">{save.isPending ? t('common.loading') : t('pivot.save', { defaultValue: 'Save' })}</button>
        </div>
      </div>
    </div>
  );
}

/** Inline manage strip — lets a saved view be deleted (hidden by default behind the load dropdown). */
function ManageViews({ wsId, views, onChanged, t }: { wsId: string; views: SavedView[]; onChanged: () => void; t: TFn }) {
  const [open, setOpen] = useState(false);
  const del = useMutation({ mutationFn: (id: string) => pivotApi.views.remove(wsId, id), onSuccess: onChanged });
  return (
    <div className="fixed bottom-4 right-4 z-30">
      {open ? (
        <div className="w-64 bg-background border border-border rounded-xl shadow-editorial p-3 space-y-1.5">
          <div className="flex items-center justify-between mb-1"><span className="text-xs font-semibold text-ink-700">{t('pivot.saved_views', { defaultValue: 'Saved views' })}</span><button onClick={() => setOpen(false)} className="text-ink-400 hover:text-ink-700"><X className="h-3.5 w-3.5" /></button></div>
          {views.map((v) => (
            <div key={v.id} className="flex items-center justify-between text-[13px] rounded border border-border/60 px-2 py-1">
              <span className="truncate text-ink-800">{v.name}</span>
              <button onClick={() => del.mutate(v.id)} className="text-ink-400 hover:text-warn-600 p-0.5"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      ) : (
        <button onClick={() => setOpen(true)} className="text-[12px] text-ink-500 bg-background border border-border rounded-full px-3 py-1.5 shadow-sm hover:text-ink-800">{t('pivot.manage_views', { defaultValue: 'Manage views' })}</button>
      )}
    </div>
  );
}

function FactsDrawer({ wsId, title, ids, onClose, fmt, t }: { wsId: string; title: string; ids: string[]; onClose: () => void; fmt: (n: number) => string; t: TFn }) {
  const { data, isLoading } = useQuery({
    queryKey: ['pivot-facts', wsId, ids.join(',')],
    queryFn: () => qaApi.facts(wsId, ids),
  });
  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal>
      <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md h-full bg-background border-l border-border shadow-editorial flex flex-col">
        <header className="h-14 px-5 flex items-center justify-between border-b border-border">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{title}</p>
            <p className="text-[11px] text-muted-foreground">{t('pivot.drill_sub', { defaultValue: 'Underlying facts (capped at 50)' })}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
          {isLoading ? <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            : !data || data.length === 0 ? <p className="text-sm text-muted-foreground">—</p>
            : data.map((f: FactRow) => (
              <div key={f.factId} className="rounded border border-border/60 px-2.5 py-1.5 text-[12.5px]">
                <div className="flex items-center justify-between">
                  <span className="num text-ink-700">{f.accountCode} · {f.scenario}</span>
                  <span className="num tabular-nums text-foreground">{fmt(f.amount)}</span>
                </div>
                <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-0.5">
                  <span className="num">{f.periodStart} → {f.periodEnd}</span>
                  {f.dim && Object.keys(f.dim).length > 0 && <span className="num truncate max-w-[160px]">{Object.entries(f.dim).map(([k, v]) => `${k}:${v}`).join(', ')}</span>}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
